/**
 * Swiss orthography normalisation.
 *
 * Swiss Standard German writes `ss`, never `ß` — *Strasse*, *gross*, *heissen*.
 * That is not a simplification we impose; it is correct Swiss orthography and what
 * this person will have written all their life. The keyboard therefore has no `ß`
 * key, and no shift state or long-press that could produce one, because a second
 * layer is a real cost for impaired motor control.
 *
 * The consequence reaches further than the keyboard: German corpora are full of
 * `ß`, so every string that can reach the screen or the prediction engine must be
 * folded first. Phase 2's lexicon build depends on this function; Phase 1's seed
 * data uses it now so the rule exists before there is a corpus to break it.
 *
 * `tests/unit/no-eszett.test.ts` asserts that no `ß` survives anywhere in the
 * shipped artefacts.
 */

/*
 * The characters below are written as escapes, not literally, so that the assertion
 * "no eszett anywhere in a shipped artefact" can stay an absolute zero with no
 * whitelist to maintain. Without this, the one legitimate occurrence — the function
 * that removes it — would appear in the bundle and force the test to allow
 * exceptions, which is how a rule like this quietly stops catching anything.
 *
 *   \u00df   LATIN SMALL LETTER SHARP S
 *   \u1e9e   LATIN CAPITAL LETTER SHARP S
 *   \u1e9b   LATIN SMALL LETTER LONG S WITH DOT ABOVE (legacy, seen in OCR)
 */
const LOWER = /[\u00df\u1e9b]/g;
const UPPER = /\u1e9e/g;
const ANY = /[\u00df\u1e9b\u1e9e]/;

/** Fold the sharp s to `ss`; the capital form to `SS`. */
export function toSwissOrthography(text: string): string {
  return text.replace(LOWER, 'ss').replace(UPPER, 'SS');
}

export function hasEszett(text: string): boolean {
  return ANY.test(text);
}

/**
 * Recursively normalise every string in a seed structure. Applied at module load,
 * so authored data is normalised before it can reach the database — one place to
 * be right rather than a discipline to remember at every call site.
 */
export function normaliseSeed<T>(value: T): T {
  if (typeof value === 'string') return toSwissOrthography(value) as T;
  if (Array.isArray(value)) return value.map((item) => normaliseSeed(item)) as T;
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, item] of Object.entries(value)) out[key] = normaliseSeed(item);
    return out as T;
  }
  return value;
}
